import React from 'react';
import Form from 'components/form';
import SearchInput from 'routes/surface/components/node-modal-form/components/ris-search-modal/components/search-input';
import { Header } from 'routes/surface/components/node-modal-form/style';
import {
  Container,
  SearchWrapper,
  Text,
  Title,
  TabContainer,
} from 'routes/surface/components/node-modal-form/components/ris-search-modal/style';
import TabbedOptions from 'routes/surface/components/node-modal-form/components/ris-search-modal/components/tabbed-options';
import { object, string } from 'yup';
import { ModalContext } from 'hoc/with-modal';
import { useTranslation } from 'i18n';
import { WithMargin } from 'components/layout';
import { RisTypeSearch } from 'graphql/types';

export interface RisSearchFormProps {
  modal: ModalContext;
  onSubmit: any;
  initialValues: {
    search: string;
    type: string;
  };
}

function createValidationSchema(t) {
  return function () {
    return object().shape({
      search: string().required(t('Search term is required')),
      type: string().required(t('Search type is required')),
    });
  };
}

function RisSearchForm({ modal, ...props }: RisSearchFormProps) {
  const { t } = useTranslation();
  const searchInput = React.useRef<any>(null);

  const validationSchema = React.useMemo(createValidationSchema(t), []);

  React.useEffect(() => {
    setTimeout(() => searchInput.current.focus(), 200);
  }, []);

  function getSearchDescription(type: RisTypeSearch) {
    switch (type) {
      case RisTypeSearch.FederalLaw:
        return (
          <Text>
            Fügen Sie dazu einfach Bundesgsetzblätter hinzu.
            zB BGBl. II Nr. 503/2020
          </Text>
        );
      case RisTypeSearch.FederalStandards:
        return (
          <Text>
            Fügen Sie dazu einfach Gesetzestext, gerichtliche Entscheidungen und Bundesgesetzblätter hinzu.
            zB §1 MRG, §879 ABGB oder Art.1 Bundes-Verfassungsgesetz, …
          </Text>
        );
      case RisTypeSearch.Judicature:
        return (
          <Text>
            Fügen Sie dazu einfach Geschäftszahl oder Rechtssatznummer hinzu.
            zB RS0132995 oder 2Ob138/19m
          </Text>
        );
      default:
        return '';
    }
  }
  return (
    <Container>
      <Form
        initialValues={props.initialValues}
        validationSchema={validationSchema}
        onSubmit={props.onSubmit}
        submitOnEnter>
        {(form) => (
          <>
            <Header>
              <Form.Button label={t('Close')} onClick={modal.hide} secondary />
              <Form.SubmitButton
                label={t('Search')}
                onClick={form.handleSubmit}
              />
            </Header>
            <SearchWrapper>
              <Title>{t('Legal Search')}</Title>
              <Text>
                {t(
                  'Insert the legal basis in the form of paragraphs, articles or business figures.',
                )}
              </Text>
              <WithMargin top="16px">
                {getSearchDescription(form.values.type)}
              </WithMargin>
              <TabContainer>
                <TabbedOptions
                  name="type"
                  options={[
                    {
                      label: t('Federal Standards'),
                      value: RisTypeSearch.FederalStandards,
                    },
                    { label: t('Judiciary'), value: RisTypeSearch.Judicature },
                    {
                      label: t('Federal Law Gazette'),
                      value: RisTypeSearch.FederalLaw,
                    },
                  ]}
                />
              </TabContainer>
              <SearchInput
                ref={searchInput}
                name="search"
                clearSearch={() => form.setFieldValue('search', '')}
                iconLeftClick={form.handleSubmit}
              />
            </SearchWrapper>
          </>
        )}
      </Form>
    </Container>
  );
}

export default RisSearchForm;
